/**
 * Camunda 7.18 Deployment REST API Service
 * https://docs.camunda.org/manual/7.18/reference/rest/deployment/
 */

import { CamundaApiClient, getCamundaApiClient } from '../CamundaApiClient';
import type {
  Deployment,
  DeploymentQuery,
  DeploymentResource,
  CountResult,
  RedeploymentDto,
} from '../types';

export class DeploymentService {
  private client: CamundaApiClient;
  private basePath = '/engine-rest/deployment';

  constructor(client?: CamundaApiClient) {
    this.client = client || getCamundaApiClient();
  }

  // ============================================================================
  // Get List
  // ============================================================================

  /**
   * Get a list of deployments
   */
  async getList(query?: DeploymentQuery): Promise<Deployment[]> {
    const params = this.buildQueryParams(query);
    return this.client.get<Deployment[]>(`${this.basePath}${params}`);
  }

  /**
   * Get deployments count
   */
  async getCount(query?: DeploymentQuery): Promise<CountResult> {
    const params = this.buildQueryParams(query);
    return this.client.get<CountResult>(`${this.basePath}/count${params}`);
  }

  // ============================================================================
  // Get Single
  // ============================================================================

  /**
   * Get deployment by ID
   */
  async getById(id: string): Promise<Deployment> {
    return this.client.get<Deployment>(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // Resources
  // ============================================================================

  /**
   * Get deployment resources
   */
  async getResources(id: string): Promise<DeploymentResource[]> {
    return this.client.get<DeploymentResource[]>(`${this.basePath}/${id}/resources`);
  }

  /**
   * Get a single deployment resource
   */
  async getResource(deploymentId: string, resourceId: string): Promise<DeploymentResource> {
    return this.client.get<DeploymentResource>(
      `${this.basePath}/${deploymentId}/resources/${resourceId}`
    );
  }

  /**
   * Get deployment resource binary data
   */
  async getResourceBinary(deploymentId: string, resourceId: string): Promise<Blob> {
    return this.client.getBlob(`${this.basePath}/${deploymentId}/resources/${resourceId}/data`);
  }

  // ============================================================================
  // Create Deployment
  // ============================================================================

  /**
   * Create a new deployment
   */
  async create(options: {
    deploymentName?: string;
    enableDuplicateFiltering?: boolean;
    deployChangedOnly?: boolean;
    deploymentSource?: string;
    tenantId?: string;
    files: File[];
  }): Promise<Deployment> {
    const formData = new FormData();
    
    if (options.deploymentName) {
      formData.append('deployment-name', options.deploymentName);
    }
    if (options.enableDuplicateFiltering) {
      formData.append('enable-duplicate-filtering', 'true');
    }
    if (options.deployChangedOnly) {
      formData.append('deploy-changed-only', 'true');
    }
    if (options.deploymentSource) {
      formData.append('deployment-source', options.deploymentSource);
    }
    if (options.tenantId) {
      formData.append('tenant-id', options.tenantId);
    }
    
    options.files.forEach((file) => {
      formData.append(file.name, file, file.name);
    });

    return this.client.postFormData<Deployment>(`${this.basePath}/create`, formData);
  }

  /**
   * Create deployment from BPMN XML string
   */
  async createFromXml(options: {
    deploymentName: string;
    fileName: string;
    bpmnXml: string;
    enableDuplicateFiltering?: boolean;
    deployChangedOnly?: boolean;
    deploymentSource?: string;
    tenantId?: string;
  }): Promise<Deployment> {
    const blob = new Blob([options.bpmnXml], { type: 'application/xml' });
    const file = new File([blob], options.fileName, { type: 'application/xml' });
    
    return this.create({
      deploymentName: options.deploymentName,
      enableDuplicateFiltering: options.enableDuplicateFiltering,
      deployChangedOnly: options.deployChangedOnly,
      deploymentSource: options.deploymentSource,
      tenantId: options.tenantId,
      files: [file],
    });
  }

  // ============================================================================
  // Redeploy
  // ============================================================================

  /**
   * Redeploy a deployment
   */
  async redeploy(id: string, dto?: RedeploymentDto): Promise<Deployment> {
    return this.client.post<Deployment>(`${this.basePath}/${id}/redeploy`, dto || {});
  }

  // ============================================================================
  // Delete
  // ============================================================================

  /**
   * Delete a deployment
   */
  async delete(
    id: string,
    options?: {
      cascade?: boolean;
      skipCustomListeners?: boolean;
      skipIoMappings?: boolean;
    }
  ): Promise<void> {
    const params = new URLSearchParams();
    if (options?.cascade) params.append('cascade', 'true');
    if (options?.skipCustomListeners) params.append('skipCustomListeners', 'true');
    if (options?.skipIoMappings) params.append('skipIoMappings', 'true');
    const queryString = params.toString();
    await this.client.delete<void>(`${this.basePath}/${id}${queryString ? `?${queryString}` : ''}`);
  }

  // ============================================================================
  // Registered Deployments
  // ============================================================================

  /**
   * Get registered deployments
   */
  async getRegistered(): Promise<string[]> {
    return this.client.get<string[]>(`${this.basePath}/registered`);
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  private buildQueryParams(query?: DeploymentQuery): string {
    if (!query) return '';
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });
    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}

// Singleton instance
let deploymentServiceInstance: DeploymentService | null = null;

export function getDeploymentService(): DeploymentService {
  if (!deploymentServiceInstance) {
    deploymentServiceInstance = new DeploymentService();
  }
  return deploymentServiceInstance;
}

export default DeploymentService;
